import type { RgbColor } from '../color/types';
import type { Grid } from '../quantize/types';

/**
 * Réglages d'image amont (Lot P2 du pivot e-commerce).
 *
 * Utilisés par les presets de style du tunnel (« Pop » pousse contraste et
 * saturation, « Noir & blanc » désature…) — appliqués sur la grille source
 * AVANT le redimensionnement (CDC §4.6-d). Jamais exposés avec ces noms au
 * grand public au lancement (les presets les pilotent).
 *
 * Chaque réglage ∈ [-1, 1], 0 = neutre :
 *  - brightness : décalage linéaire (±255 aux extrêmes)
 *  - contrast   : dilatation autour du gris moyen (128)
 *  - saturation : mélange avec la luminance (Rec. 709) ; -1 = niveaux de gris
 *
 * Ordre d'application : brightness → contrast → saturation (ordre stable,
 * documenté, pour que les presets soient reproductibles).
 */
export type ImageAdjustments = {
  brightness?: number;
  contrast?: number;
  saturation?: number;
};

export function isNeutral(adj: ImageAdjustments | undefined): boolean {
  if (!adj) return true;
  return (adj.brightness ?? 0) === 0 && (adj.contrast ?? 0) === 0 && (adj.saturation ?? 0) === 0;
}

export function adjustGrid(grid: Grid, adj: ImageAdjustments): Grid {
  if (isNeutral(adj)) return grid;

  const brightness = clampUnit(adj.brightness ?? 0) * 255;
  // Facteur de contraste : -1 → 0 (tout gris), 0 → 1 (neutre), 1 → 3 (fort).
  const c = clampUnit(adj.contrast ?? 0);
  const contrastFactor = c >= 0 ? 1 + c * 2 : 1 + c;
  const s = clampUnit(adj.saturation ?? 0);
  const saturationFactor = 1 + s;

  const pixels: RgbColor[] = new Array<RgbColor>(grid.pixels.length);
  for (let i = 0; i < grid.pixels.length; i++) {
    const p = grid.pixels[i];
    if (p === undefined) {
      throw new Error(`Pixel manquant à l'index ${i}.`);
    }
    let r = p.r + brightness;
    let g = p.g + brightness;
    let b = p.b + brightness;

    r = (r - 128) * contrastFactor + 128;
    g = (g - 128) * contrastFactor + 128;
    b = (b - 128) * contrastFactor + 128;

    // Luminance Rec. 709 sur les valeurs courantes (déjà ajustées).
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    r = luma + (r - luma) * saturationFactor;
    g = luma + (g - luma) * saturationFactor;
    b = luma + (b - luma) * saturationFactor;

    pixels[i] = {
      r: clamp255(Math.round(r)),
      g: clamp255(Math.round(g)),
      b: clamp255(Math.round(b)),
    };
  }

  return { width: grid.width, height: grid.height, pixels };
}

function clampUnit(v: number): number {
  if (!Number.isFinite(v)) return 0;
  if (v < -1) return -1;
  if (v > 1) return 1;
  return v;
}

function clamp255(v: number): number {
  if (v < 0) return 0;
  if (v > 255) return 255;
  return v;
}
